import { useMutation } from "@apollo/client";
import { MoreHorizontal, Edit, UserMinus, Mail, Trash } from "lucide-react";
import { useState } from "react";
import { updateBusinessUnitUserMutation } from "./api/mutations";
import { type businessUnitUsersQuery } from "./api/queries";
import { UserEditForm } from "./UserEditForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { graphql, type ResultOf } from "@/graphql";
import { useToast } from "@/hooks/use-toast";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";

type User = NonNullable<NonNullable<ResultOf<typeof businessUnitUsersQuery>["businessUnitUsers"]>[number]>;

interface UserActionMenuProps {
  user: User;
  onUserUpdated?: () => void;
}

type ActionType = "edit" | "deactivate" | "reactivate" | "delete" | null;

export function UserActionMenu({ user, onUserUpdated }: UserActionMenuProps) {
  const [confirmAction, setConfirmAction] = useState<ActionType>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { toast } = useToast();

  const [resendInvite, { loading: resendLoading }] = useMutation(resendInviteMutation);

  const [updateUser, { loading: updateLoading }] = useMutation(updateBusinessUnitUserMutation);

  const [deleteUser, { loading: deleteLoading }] = useMutation(deleteUserFromFarmMutation);

  const handleResendInvite = async () => {
    try {
      const result = await resendInvite({
        variables: {
          input: {
            businessUnitUser: parseInt(user.id),
          },
        },
      });

      const errors = result.errors || [];
      if (errors.length > 0) {
        toast({
          title: "Failed to resend invitation",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });
        return;
      }

      if (result.data?.reInviteUser?.errors?.length) {
        toast({
          title: "Failed to resend invitation",
          description: extractErrorMessages(result.data.reInviteUser.errors),
          variant: "destructive",
        });
        return;
      }

      if (result.data?.reInviteUser?.success) {
        toast({
          title: "Invitation resent",
          description: `Successfully resent invitation to ${user.user.firstName} ${user.user.lastName}`,
        });
      }
    } catch (error) {
      toast({
        title: "Failed to resend invitation",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleToggleUserStatus = async (newStatus: "ACTIVE" | "INACTIVE") => {
    try {
      const result = await updateUser({
        variables: {
          input: {
            businessUnitUser: parseInt(user.id),
            role: user.role?.id ? parseInt(user.role.id) : 1, // Preserve current role, fallback to 1 if no role
            status: newStatus,
          },
        },
      });

      const errors = result.errors || [];
      if (errors.length > 0) {
        toast({
          title: "Failed to update user",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });
        return;
      }

      if (result.data?.updateBusinessUnitUser?.errors?.length) {
        toast({
          title: "Failed to update user",
          description: extractErrorMessages(result.data.updateBusinessUnitUser.errors),
          variant: "destructive",
        });
        return;
      }

      if (result.data?.updateBusinessUnitUser?.businessUnitUser) {
        toast({
          title: "User updated",
          description: `Successfully updated ${user.user.firstName} ${user.user.lastName}`,
        });
        onUserUpdated?.();
        setConfirmAction(null);
      }
    } catch (error) {
      toast({
        title: "Failed to update user",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = async () => {
    try {
      const result = await deleteUser({
        variables: {
          input: {
            businessUnitUser: parseInt(user.id),
          },
        },
      });

      const errors = result.errors || [];
      if (errors.length > 0) {
        toast({
          title: "Failed to remove user",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });
        return;
      }

      if (result.data?.deleteUserFromFarm?.errors?.length) {
        toast({
          title: "Failed to remove user",
          description: extractErrorMessages(result.data.deleteUserFromFarm.errors),
          variant: "destructive",
        });
        return;
      }

      if (result.data?.deleteUserFromFarm?.success) {
        toast({
          title: "User removed",
          description: `Successfully removed ${user.user.firstName} ${user.user.lastName} from the business unit`,
        });
        onUserUpdated?.();
        setConfirmAction(null);
      }
    } catch (error) {
      toast({
        title: "Failed to remove user",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleEditUser = () => {
    setEditModalOpen(true);
  };

  const isLoading = resendLoading || updateLoading || deleteLoading;
  const isInvited = user.status === "INVITED";
  const canDeactivate = user.status === "ACTIVE" && !user.isOrganisationOwner;
  const canReactivate = user.status === "INACTIVE";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0" disabled={isLoading}>
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleEditUser}>
            <Edit className="mr-2 h-4 w-4" />
            Edit user
          </DropdownMenuItem>
          {isInvited && (
            <DropdownMenuItem onClick={handleResendInvite} disabled={resendLoading}>
              <Mail className="mr-2 h-4 w-4" />
              Resend invitation
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {canDeactivate && (
            <DropdownMenuItem onClick={() => setConfirmAction("deactivate")} disabled={updateLoading}>
              <UserMinus className="mr-2 h-4 w-4" />
              Deactivate user
            </DropdownMenuItem>
          )}
          {canReactivate && (
            <DropdownMenuItem onClick={() => setConfirmAction("reactivate")} disabled={updateLoading}>
              <UserMinus className="mr-2 h-4 w-4" />
              Reactivate user
            </DropdownMenuItem>
          )}
          {user.isOrganisationOwner ? null : (
            <DropdownMenuItem
              onClick={() => setConfirmAction("delete")}
              className="text-destructive focus:text-destructive"
              disabled={deleteLoading}
            >
              <Trash className="mr-2 h-4 w-4" />
              Remove from business unit
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog open={confirmAction === "deactivate"} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate {user.user.firstName} {user.user.lastName}? They will lose access to
              this business unit but can be reactivated later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleToggleUserStatus("INACTIVE")} disabled={updateLoading}>
              {updateLoading ? "Deactivating..." : "Deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivate Confirmation Dialog */}
      <AlertDialog open={confirmAction === "reactivate"} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reactivate {user.user.firstName} {user.user.lastName}? They will regain access to
              this business unit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleToggleUserStatus("ACTIVE")} disabled={updateLoading}>
              {updateLoading ? "Reactivating..." : "Reactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={confirmAction === "delete"} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User from Business Unit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently remove {user.user.firstName} {user.user.lastName}
              from this business unit? This action cannot be undone and they will need to be re-invited to regain
              access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? "Removing..." : "Remove User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit User Modal */}
      <UserEditForm user={user} open={editModalOpen} onOpenChange={setEditModalOpen} onSuccess={onUserUpdated} />
    </>
  );
}

const resendInviteMutation = graphql(`
  mutation ResendInvite($input: ReInviteUserInput!) {
    reInviteUser(input: $input) {
      success
      errors {
        field
        message
      }
    }
  }
`);

const deleteUserFromFarmMutation = graphql(`
  mutation DeleteUserFromFarm($input: DeleteUserFromFarmInput!) {
    deleteUserFromFarm(input: $input) {
      success
      errors {
        field
        message
      }
    }
  }
`);
